import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Brain, Timer, Trophy, RotateCcw } from 'lucide-react';

const MemoryGame = () => {
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const cardSymbols = ['⚡', '🧠', '💻', '🔮', '🎯', '🚀', '🌟', '💎', '🎮', '🔋', '🌐', '⚙️'];

  const initializeGame = useCallback(() => {
    const levelConfig = {
      1: { pairs: 4, gridSize: 4 },
      2: { pairs: 6, gridSize: 4 },
      3: { pairs: 8, gridSize: 4 },
      4: { pairs: 10, gridSize: 5 },
      5: { pairs: 12, gridSize: 5 }
    };

    const config = levelConfig[level] || levelConfig[1];
    const selectedSymbols = cardSymbols.slice(0, config.pairs);
    const gameCards = [...selectedSymbols, ...selectedSymbols]
      .sort(() => Math.random() - 0.5)
      .map((symbol, index) => ({
        id: index,
        symbol,
        isFlipped: false,
        isMatched: false
      }));

    setCards(gameCards);
    setFlippedCards([]);
    setMatchedPairs([]);
    setScore(0);
    setTime(0);
    setIsPlaying(false);
    setGameStarted(false);
  }, [level]);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  useEffect(() => {
    let interval;
    if (isPlaying && gameStarted) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameStarted]);

  const startGame = () => {
    setIsPlaying(true);
    setGameStarted(true);
  };

  const handleCardClick = (cardId) => {
    if (!isPlaying || flippedCards.length >= 2) return;

    const card = cards.find(c => c.id === cardId);
    if (card.isFlipped || card.isMatched) return;

    const newCards = cards.map(c => 
      c.id === cardId ? { ...c, isFlipped: true } : c
    );
    setCards(newCards);

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setTimeout(() => {
        checkMatch(newFlippedCards);
      }, 1000);
    }
  };

  const checkMatch = (flippedCardIds) => {
    const [firstId, secondId] = flippedCardIds;
    const firstCard = cards.find(c => c.id === firstId);
    const secondCard = cards.find(c => c.id === secondId);

    if (firstCard.symbol === secondCard.symbol) {
      // Match found
      setMatchedPairs(prev => [...prev, firstCard.symbol]);
      setScore(prev => prev + 100);
      
      const newCards = cards.map(c => 
        c.id === firstId || c.id === secondId 
          ? { ...c, isMatched: true }
          : c
      );
      setCards(newCards);

      // Check if level is complete
      const levelConfig = {
        1: { pairs: 4 },
        2: { pairs: 6 },
        3: { pairs: 8 },
        4: { pairs: 10 },
        5: { pairs: 12 }
      };
      
      if (matchedPairs.length + 1 >= levelConfig[level].pairs) {
        setTimeout(() => {
          if (level < 5) {
            setLevel(prev => prev + 1);
          } else {
            // Game completed
            setIsPlaying(false);
          }
        }, 1000);
      }
    } else {
      // No match, flip cards back
      const newCards = cards.map(c => 
        c.id === firstId || c.id === secondId 
          ? { ...c, isFlipped: false }
          : c
      );
      setCards(newCards);
      setScore(prev => Math.max(0, prev - 10));
    }

    setFlippedCards([]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const resetGame = () => {
    initializeGame();
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Brain className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              {t('memory.reset')}
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!gameStarted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            {t('memory.title')}
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            {t('memory.instructions')}
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            {t('memory.startGame')}
          </button>
        </motion.div>
      )}

      {/* Game Grid */}
      <div className="cyber-card p-6">
        <div 
          className="grid gap-3 mx-auto"
          style={{
            gridTemplateColumns: `repeat(${level <= 3 ? 4 : 5}, 1fr)`,
            maxWidth: level <= 3 ? '400px' : '500px'
          }}
        >
          <AnimatePresence>
            {cards.map((card) => (
              <motion.div
                key={card.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  aspect-square cursor-pointer rounded-lg border-2 transition-all duration-300 flex items-center justify-center text-4xl font-bold
                  ${card.isMatched 
                    ? 'bg-neon-green/20 border-neon-green text-neon-green' 
                    : card.isFlipped 
                      ? 'bg-neon-blue/20 border-neon-blue text-neon-blue' 
                      : 'bg-dark-100 border-neon-blue/30 text-transparent hover:border-neon-blue/50'
                  }
                `}
                onClick={() => handleCardClick(card.id)}
              >
                {card.isFlipped || card.isMatched ? card.symbol : '?'}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Level Progress */}
      <div className="cyber-card p-6 mt-8">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            {t('memory.levelProgress')}
          </h4>
          <div className="w-full bg-dark-100 rounded-full h-4 border border-neon-blue/30">
            <motion.div
              className="bg-gradient-to-r from-neon-blue to-neon-green h-full rounded-full transition-all duration-500"
              initial={{ width: 0 }}
              animate={{ width: `${(matchedPairs.length / (level <= 3 ? 4 : level === 4 ? 10 : 12)) * 100}%` }}
            />
          </div>
          <p className="text-gray-300 font-tech mt-2">
            {matchedPairs.length} / {level <= 3 ? 4 : level === 4 ? 10 : 12} {t('memory.pairsFound')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MemoryGame;
